import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, TextField, Grid,
  TablePagination, Alert, CircularProgress, Snackbar, FormControl,
  InputLabel, Select, MenuItem
} from '@mui/material';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import { 
  createDepartment, 
  getAllDepartments, 
  getDepartmentById,
  updateDepartment, 
  deleteDepartment,
  activateDepartment
} from '../api/services/userService/Department';
import { getAllClients, getClientById } from '../api/services/userService/Client';
import { getCurrentUser } from '../api/services/userService/User';

export default function DepartmentManagement({ userPermissions }) {
  const { keycloak } = useKeycloak();
  const [departments, setDepartments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentDepartment, setCurrentDepartment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    clientId: ''
  });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Snackbar notification
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Check if user can manage departments
  const canManageDepartments = () => {
    if (!userPermissions) return false;
    return userPermissions.isSuperAdmin || 
           (userPermissions.clientIds.length > 0 && 
            userPermissions.clientIds.some(id => userPermissions.clientRoles[id] === 'admin')) ||
           (userPermissions.departmentIds.length > 0 && 
            userPermissions.departmentIds.some(id => userPermissions.departmentRoles[id] === 'admin'));
  };

  // Filter departments based on user permissions
  const filterDepartmentsByPermissions = (departmentsList) => {
    if (!userPermissions || userPermissions.isSuperAdmin) {
      return departmentsList;
    }
    
    const accessibleDepartments = departmentsList.filter(dept => {
      // User has direct access to this department
      if (userPermissions.departmentIds.includes(dept.id)) {
        return true;
      }
      
      // User has access to the client that owns this department
      if (userPermissions.clientIds.includes(dept.clientId)) {
        return true;
      }
      
      return false;
    });
    
    return accessibleDepartments;
  };

  // Filter clients based on user permissions
  const filterClientsByPermissions = (clientsList) => {
    if (!userPermissions || userPermissions.isSuperAdmin) {
      return clientsList;
    }
    
    return clientsList.filter(client => 
      userPermissions.clientIds.includes(client.id)
    );
  };

  // Fetch clients for dropdown
  const fetchClients = async () => {
    try {
      await keycloak.updateToken(30);
      
      let clients = [];
      
      if (userPermissions?.isSuperAdmin) {
        const response = await getAllClients({
          pagination: {
            page: 1,
            pageSize: 100
          },
          sorting: {
            sort_by: 'SORT_BY_NAME',
            order: 'SORT_ORDER_ASCENDING'
          }
        }, keycloak.token);
        
        if (response.success) {
          clients = response.clients;
        } else {
          setError(response.error);
        }
      } else {
        const currentUserResponse = await getCurrentUser(keycloak.token);
        
        if (!currentUserResponse.success) {
          throw new Error(currentUserResponse.error || 'Failed to fetch current user data');
        }

        const userData = currentUserResponse.user;
        console.log('Current user data for DepartmentManagement:', userData);

        if (userData.client) {
          const clientResponse = await getClientById(userData.client.client.id, keycloak.token);
          if (clientResponse.success) {
            clients = [clientResponse.client];
          }
        }
      }
      
      const filteredClients = filterClientsByPermissions(clients);
      setClients(filteredClients);
    } catch (err) {
      setError(err.message || 'Failed to fetch clients');
    }
  };

  // Fetch departments based on filters
  const fetchDepartments = async () => {
    setLoading(true);
    try {
      await keycloak.updateToken(30);
      
      let departments = [];
      
      if (userPermissions?.isSuperAdmin) {
        const params = {
          pagination: {
            page: page + 1,
            pageSize: rowsPerPage
          },
          sorting: {
            sort_by: 'SORT_BY_NAME',
            order: 'SORT_ORDER_ASCENDING'
          }
        };
        
        if (searchTerm) {
          params.nameFilter = searchTerm;
        }
        
        if (selectedClientId) {
          params.clientId = selectedClientId;
        }
        
        console.log('Fetching departments with params:', params);
        const response = await getAllDepartments(params, keycloak.token);
        console.log('Departments response:', response);
        
        if (response.success) {
          departments = response.departments;
          setTotalCount(response.pagination.totalCount);
        } else {
          setError(response.error);
        }
      } else {
        const currentUserResponse = await getCurrentUser(keycloak.token);
        
        if (!currentUserResponse.success) {
          throw new Error(currentUserResponse.error || 'Failed to fetch current user data');
        }

        const userData = currentUserResponse.user;
        console.log('Current user data for departments:', userData);

        if (userData.departments && userData.departments.length > 0) {
          const departmentPromises = userData.departments.map(deptData => 
            getDepartmentById(deptData.department.id, keycloak.token)
          );
          const departmentResponses = await Promise.all(departmentPromises);
          
          departmentResponses.forEach((response) => {
            if (response.success) {
              const department = response.department;
              department.clientId = userData.client ? userData.client.client.id : null;
              departments.push(department);
            }
          });
        }
        
        if (searchTerm) {
          departments = departments.filter(dept => 
            dept.name.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
        
        if (selectedClientId) {
          departments = departments.filter(dept => dept.clientId === selectedClientId);
        }
        
        setTotalCount(departments.length);
      }
      
      // Apply permission-based filtering
      const permissionFilteredDepartments = filterDepartmentsByPermissions(departments);
      
      setDepartments(permissionFilteredDepartments);
      
      if (!userPermissions?.isSuperAdmin) {
        setTotalCount(permissionFilteredDepartments.length);
      }
      
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError(err.message || 'Failed to fetch departments');
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchClients();
  }, [keycloak.token]);

  // Fetch departments when page, rowsPerPage, or client selection changes
  useEffect(() => {
    fetchDepartments();
  }, [page, rowsPerPage, selectedClientId, searchTerm, keycloak.token]);

  const handleSearch = () => {
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClientFilterChange = (event) => {
    setSelectedClientId(event.target.value);
    setPage(0);
  };

  const openCreateDialog = () => {
    setIsEdit(false);
    setFormData({
      name: '',
      clientId: ''
    });
    setOpenDialog(true);
  };

  const openEditDialog = (department) => {
    setIsEdit(true);
    setCurrentDepartment(department);
    setFormData({
      name: department.name,
      clientId: department.clientId
    });
    setOpenDialog(true);
  };

  const handleDeleteClick = (department) => {
    setCurrentDepartment(department);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleActivate = async (departmentId) => {
    try {
      await keycloak.updateToken(30);
      
      const response = await activateDepartment(departmentId, keycloak.token);
      
      if (response.success) {
        setNotification({
          open: true,
          message: 'Department activated successfully',
          severity: 'success'
        });
        fetchDepartments();
      } else {
        setNotification({
          open: true,
          message: response.error || 'Failed to activate department',
          severity: 'error'
        });
      }
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || 'An error occurred',
        severity: 'error'
      });
    }
  };

  const handleSubmit = async () => {
    try {
      await keycloak.updateToken(30);
      
      if (isEdit) {
        const response = await updateDepartment({
          id: currentDepartment.id,
          name: formData.name
        }, keycloak.token);
        
        if (response.success) {
          setNotification({
            open: true,
            message: 'Department updated successfully',
            severity: 'success'
          });
          fetchDepartments();
        } else {
          setNotification({
            open: true,
            message: response.error || 'Failed to update department',
            severity: 'error'
          });
        }
      } else {
        const response = await createDepartment({
          name: formData.name,
          clientId: formData.clientId
        }, keycloak.token);
        
        if (response.success) {
          setNotification({
            open: true,
            message: 'Department created successfully',
            severity: 'success'
          });
          fetchDepartments();
        } else {
          setNotification({
            open: true,
            message: response.error || 'Failed to create department',
            severity: 'error'
          });
        }
      }
      
      setOpenDialog(false);
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || 'An error occurred',
        severity: 'error'
      });
    }
  };

  const handleDelete = async () => {
    try {
      await keycloak.updateToken(30);
      
      const response = await deleteDepartment(currentDepartment.id, keycloak.token);
      
      if (response.success) {
        setNotification({
          open: true,
          message: 'Department deleted successfully',
          severity: 'success'
        });
        fetchDepartments();
      } else {
        setNotification({
          open: true,
          message: response.error || 'Failed to delete department',
          severity: 'error'
        });
      }
      
      setOpenDeleteDialog(false);
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || 'An error occurred',
        severity: 'error'
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  // Get client name by ID
  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown Client';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Department Management</Typography>
        {canManageDepartments() && (
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={openCreateDialog}
          >
            Add Department
          </Button>
        )}
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            variant="outlined"
            label="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton onClick={handleSearch}>
                  <Search />
                </IconButton>
              )
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel id="client-filter-label">Filter by Client</InputLabel>
            <Select
              labelId="client-filter-label"
              value={selectedClientId}
              label="Filter by Client"
              onChange={handleClientFilterChange}
            >
              <MenuItem value="">
                <em>All Clients</em>
              </MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  {canManageDepartments() && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {departments.length > 0 ? (
                  departments.map((department) => (
                    <TableRow key={department.id}>
                      <TableCell>{department.name}</TableCell>
                      <TableCell>{getClientName(department.clientId)}</TableCell>
                      <TableCell>{new Date(department.createdAt).toLocaleString()}</TableCell>
                      <TableCell>{new Date(department.updatedAt).toLocaleString()}</TableCell>
                      {canManageDepartments() && (
                        <TableCell align="right">
                          <IconButton onClick={() => openEditDialog(department)}>
                            <Edit />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteClick(department)}>
                            <Delete />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={canManageDepartments() ? 5 : 4} align="center">
                      No departments found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelDisplayedRows={({ from, to, count }) => {
              // When client-side filtering is active, adjust the display
              if (selectedClientId) {
                const adjustedFrom = count === 0 ? 0 : (page * rowsPerPage) + 1;
                const adjustedTo = Math.min((page + 1) * rowsPerPage, count);
                return `${adjustedFrom}-${adjustedTo} of ${count}`;
              }
              return `${from}-${to} of ${count !== -1 ? count : 'more than ' + to}`;
            }}
          />
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEdit ? 'Edit Department' : 'Create New Department'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Department Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          {!isEdit && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="client-select-label">Client</InputLabel>
              <Select
                labelId="client-select-label"
                name="clientId"
                value={formData.clientId}
                label="Client"
                onChange={handleInputChange}
              >
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete department "{currentDepartment?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 